import { Button, Chip, Group, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BED_TYPES } from 'src/Constants/bedTypes';
import { useCreateBed, useUpdateBed } from 'src/Hooks/Mutations/useCartographyMutations';
import { BedType } from 'src/types/cartography.types';

import styles from './BedForm.module.scss';
import { Props } from './BedForm.types';

const BED_TYPE_VALUES = Object.values(BedType);

const BedForm = ({ roomId, bed, onDone }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!bed;

  const [type, setType] = useState<BedType>(bed?.type ?? BedType.DOUBLE);
  const [widthCm, setWidthCm] = useState<number | ''>(
    bed?.widthCm ?? BED_TYPES[BedType.DOUBLE].defaultWidthCm
  );
  const [lengthCm, setLengthCm] = useState<number | ''>(
    bed?.lengthCm ?? BED_TYPES[BedType.DOUBLE].defaultLengthCm
  );
  const [label, setLabel] = useState(bed?.label ?? '');

  // Reset to the bed being edited when it switches (user clicks another « Modifier »).
  const editedBedId = bed?.id;
  useEffect(() => {
    if (!bed) return;
    setType(bed.type);
    setWidthCm(bed.widthCm ?? '');
    setLengthCm(bed.lengthCm ?? '');
    setLabel(bed.label ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bed is captured via editedBedId
  }, [editedBedId]);

  const handleTypeChange = (next: BedType) => {
    setType(next);
    if (!isEdit) {
      const defaults = BED_TYPES[next];
      setWidthCm(defaults.defaultWidthCm);
      setLengthCm(defaults.defaultLengthCm);
    }
  };

  const notifySaved = () => {
    notifications.show({
      message: t('admin.structure.beds.savedNotification', 'Couchage enregistré'),
      color: 'teal',
    });
  };

  const { mutate: createBed, isLoading: isCreating } = useCreateBed({
    onSuccess: () => {
      notifySaved();
      onDone();
    },
  });
  const { mutate: updateBed, isLoading: isUpdating } = useUpdateBed({
    onSuccess: () => {
      notifySaved();
      onDone();
    },
  });

  const capacity = BED_TYPES[type].capacity;

  const handleSubmit = () => {
    const payload = {
      type,
      widthCm: widthCm === '' ? undefined : Number(widthCm),
      lengthCm: lengthCm === '' ? undefined : Number(lengthCm),
      label: label.trim() ? label.trim() : undefined,
    };
    if (isEdit && bed) {
      updateBed({ bedId: bed.id, payload });
    } else {
      createBed({ roomId, payload });
    }
  };

  return (
    <div className={styles.container}>
      <Stack gap="md">
        <div>
          <Text className={styles.title} mb="xs">
            {isEdit
              ? t('admin.structure.beds.modal.editTitle', 'Modifier le couchage')
              : t('admin.structure.beds.modal.createTitle', 'Nouveau couchage')}
          </Text>
          <Text size="sm" fw={500} mb="xs">
            {t('admin.structure.beds.modal.type', 'Type de lit')}
          </Text>
          <Chip.Group multiple={false} value={type} onChange={v => handleTypeChange(v as BedType)}>
            <div className={styles.chips}>
              {BED_TYPE_VALUES.map(v => (
                <Chip key={v} value={v} variant="light" size="sm">
                  {t(`admin.structure.bedTypes.${v}`, v)}
                </Chip>
              ))}
            </div>
          </Chip.Group>
        </div>

        <Group grow>
          <NumberInput
            label={t('admin.structure.beds.modal.widthCm', 'Largeur (cm)')}
            value={widthCm}
            onChange={value => setWidthCm(value === '' ? '' : Number(value))}
            min={0}
          />
          <NumberInput
            label={t('admin.structure.beds.modal.lengthCm', 'Longueur (cm)')}
            value={lengthCm}
            onChange={value => setLengthCm(value === '' ? '' : Number(value))}
            min={0}
          />
        </Group>

        <TextInput
          label={t('admin.structure.beds.modal.label', 'Libellé (optionnel)')}
          placeholder={t(
            'admin.structure.beds.modal.labelPlaceholder',
            'Lit du fond, lit côté fenêtre…'
          )}
          value={label}
          onChange={e => setLabel(e.currentTarget.value)}
        />

        <div className={styles.capacityRow}>
          {t('admin.structure.metrics.capacity', "Capacité d'accueil")} :{' '}
          <strong>
            {t('admin.structure.beds.capacity', {
              count: capacity,
              defaultValue: '{{count}} personne',
              defaultValue_other: '{{count}} personnes',
            })}
          </strong>
        </div>

        <Group justify="space-between">
          <Button variant="outline" onClick={onDone}>
            {t('admin.structure.beds.modal.cancel', 'Annuler')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isCreating || isUpdating}>
            {t('admin.structure.beds.modal.save', 'Enregistrer')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
};

export default BedForm;
